/**
 * Snap indicator — small visual marker at the active snap target position.
 *
 * Renders a billboarded ring that maintains a constant screen-space size.
 * Color-coded by snap target type. Self-hides when no snap result is active.
 *
 * Reads the last snap result from the module-level slot in modeling/snap.ts
 * every frame via useFrame — no React state needed for the position update.
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import {
  Discard,
  length,
  max,
  mul,
  smoothstep,
  sub,
  uniform,
  uv,
} from "three/tsl";
import * as THREE from "three/webgpu";
import type { SnapTarget } from "../../app/storeTypes.ts";
import { getLastSnapResult } from "../../modeling/snap.ts";
import { orientBillboardToCamera } from "../billboard.ts";
import { useDispose } from "../hooks/useDispose.ts";
import { screenToWorldRadius } from "../screenRadius.ts";

// ---------------------------------------------------------------------------
// Colors per snap target type
// ---------------------------------------------------------------------------

const SNAP_COLORS: Record<SnapTarget, THREE.Color> = {
  grid: new THREE.Color(0xc0c0c0), // silver
  vertex: new THREE.Color(0x00e5ff), // cyan
  edge: new THREE.Color(0x4cff82), // green
  face: new THREE.Color(0x5aa0e8), // blue
  surface: new THREE.Color(0xff4d94), // magenta
  object: new THREE.Color(0xf0a030), // orange
};

// Shared geometry — tiny quad
const _planeGeo = new THREE.PlaneGeometry(1, 1);

// Scratch objects
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SnapIndicator() {
  const meshRef = useRef<THREE.Mesh>(null!);
  const { camera, size } = useThree();

  const uColor = useMemo(() => uniform(new THREE.Color(0x00e5ff)), []);

  const material = useMemo(() => {
    // UV mapped to centered coords: [-0.5, 0.5]
    const centered = sub(uv(), 0.5);
    // dist = length(centered) * 2.0 — 0 at center, 1 at edge
    const dist = mul(length(centered), 2.0);

    // Ring: visible between 0.5 and 0.9
    const ring = mul(smoothstep(0.4, 0.55, dist), smoothstep(1.0, 0.85, dist));
    // Filled dot in center
    const dot = smoothstep(0.25, 0.0, dist);

    // Final alpha = max(ring * 0.9, dot * 0.7)
    const alpha = max(mul(ring, 0.9), mul(dot, 0.7));

    // Discard if alpha < 0.01
    Discard(alpha.lessThan(0.01));

    const mat = new THREE.MeshBasicNodeMaterial();
    mat.colorNode = uColor;
    mat.opacityNode = alpha;
    mat.transparent = true;
    mat.depthTest = false;
    mat.depthWrite = false;
    mat.side = THREE.DoubleSide;

    return mat;
  }, [uColor]);
  useDispose(material);

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const result = getLastSnapResult();
    if (!result) {
      mesh.visible = false;
      return;
    }

    mesh.visible = true;

    // Position at the snap target
    const [wx, wy, wz] = result.worldPosition;
    _pos.set(wx, wy, wz);
    mesh.position.copy(_pos);

    // Constant screen-space size: ~8px at any distance
    mesh.scale.setScalar(screenToWorldRadius(10, _pos, camera, size.height));

    orientBillboardToCamera(mesh, camera, _quat);

    // Update color via the uniform node's value
    const color = SNAP_COLORS[result.hitType];
    uColor.value.copy(color);
  });

  return (
    <mesh
      ref={meshRef}
      geometry={_planeGeo}
      material={material}
      visible={false}
      renderOrder={1000}
      frustumCulled={false}
    />
  );
}
